import { CourseStepResponse } from './CourseStepResponse';
import { CompactUserResponse, UserId, VeryCompactUserResponse } from '../account';
import { PageQuery } from '../common';
import { CourseBuilderBlockFormData } from '@modules/course/CourseBuilder/CourseBuilderPage';
import { FolderBreadcrumbResponse, FolderId, FolderStructureResponse } from '../file';
import { TenantId } from '../tenant';
import { CompanyId } from '../companies';

export type CourseId = string;

export enum CourseType {
  active = 'active',
  archive = 'archive',
}

export enum CourseStatus {
  all = 'all',
  publish = 'published',
  unpublish = 'un-published',
}

export const courseTypes = [CourseType.active, CourseType.archive];
export const courseStatuses = [CourseStatus.all, CourseStatus.publish, CourseStatus.unpublish];

export interface CourseSearchParams extends PageQuery {
  userId?: UserId;
  parentId?: string;
  search?: string;
  type?: CourseType;
  status?: CourseStatus;
  isCompleted?: boolean;
  isCompact?: boolean;
  dateRange?: string;
}

export interface CourseEnrollerResponse {
  id: CourseId;
  uid: UserId;
  user: CompactUserResponse;
  isCompleted: boolean;
  progress: number;
  lastVisit?: number;
  completedAt?: number;
  version?: number;
  versionWhen?: number;
  url?: string;
  tenantName?: string;
  tenantId?: TenantId;
  isPendingGrade?: boolean;
  totalTimeSpent?: number; // in seconds
}

export interface CourseResponse {
  id: CourseId;
  title: string;
  description: string;
  type: CourseType;
  status: CourseStatus;
  sections: CourseSectionResponse[];
  users?: CourseEnrollerResponse[]; // Deprecated - use fetchCourseEnrollers() instead
  enrollersCount?: number; // Quick count for display
  my?: CourseEnrollerResponse;
  settings: CourseSettingsResponse;
  menuOrder: number;
  isFolder?: boolean;
  folderId?: FolderId;
  lastUpdated?: Date;
  breadcrumbs?: FolderBreadcrumbResponse[];
  tenantName?: string;
  totalItems?: number;
  instructors: VeryCompactUserResponse[];
}

export interface CourseFolderResponse {
  id: string;
  title: string;
  userId: UserId;
}

export interface CourseFolderStructureResponse {
  id: string;
  name: string;
  folders: FolderStructureResponse[];
  breadcrumbs?: FolderBreadcrumbResponse[];
  tenantName?: string;
  tenantId?: TenantId;
}

export interface ShareCourseResponse {
  email: string;
  appId: string;
  companyId: CompanyId;
  courseId: CourseId;
}

export type VeryCompactCourseResponse = Pick<CourseResponse, 'id' | 'title' | 'description'> & {
  isEnrolled: boolean;
};

export interface CourseSettingsResponse {
  completeMessage: string;
}

export type CourseSectionId = number;
export interface CourseSectionResponse {
  id: CourseSectionId;
  name: string;
  menuOrder?: number;
  steps: CourseStepResponse[];
  isClosed?: boolean;
}

export interface CourseFolderBreadcrumbResponse {
  id: string;
  name: string;
}

export interface CourseResponseItems {
  breadcrumb: CourseFolderBreadcrumbResponse[];
  items: CompactCourseResponse[];
}

export type CompactCourseResponse = CourseResponse;

export interface UserCourseCountResponse {
  userCourses: number;
  lineupCourses: number;
}

export interface CopyCourseTemplateRequest {
  title: string;
  data: CourseBuilderBlockFormData[];
}

export interface CourseBlockTemplateResponse {
  id: string;
  title: string;
  data: CourseBuilderBlockFormData[];
}

/** NEW: Enrollers Pagination Types **/
export enum EnrollerStatus {
  all = 'all',
  inProgress = 'in-progress',
  completed = 'completed',
  pendingGrades = 'pending-grades',
}

export enum EnrollerSortBy {
  name = 'name',
  progress = 'progress',
  lastVisit = 'lastVisit',
  completedAt = 'completedAt',
}

export interface EnrollersSearchParams extends PageQuery {
  search?: string;
  status?: EnrollerStatus;
  sortBy?: EnrollerSortBy;
  sortDirection?: 'asc' | 'desc';
}
